import React, { useState, useEffect, useRef, useCallback } from 'react';

// Define the metrics data (can be moved outside if static)
const metricsData = [
  {
    id: 'polymers',
    target: 20,
    suffix: '+',
    text: 'polymer types analysed with AI precision',
    duration: 1500 // ms
  },
  {
    id: 'methods',
    target: 3,
    suffix: '',
    text: 'spectroscopic methods supported: ATR-FTIR, Raman, Reflectance',
    duration: 1000
  },
  {
    id: 'accuracy',
    target: 95,
    suffix: '%',
    text: 'accuracy in microplastic identification',
    duration: 2000
  },
  {
    id: 'samples',
    target: 10000,
    suffix: '+',
    text: 'samples analysed in our spectral library',
    display: '10K+',
    duration: 2500
  },
  {
    id: 'researchers',
    target: 100,
    suffix: '+',
    text: 'researchers actively using our platform',
    duration: 1800
  },
  {
    id: 'time',
    target: 150,
    suffix: ' ms', // Added space for clarity
    text: 'Average analysis time per spectrum vs. hours spent on in-house scripting.',
    duration: 1200
  }
];

// Easing function: easeOutCubic
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const AnimatedMetricsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false); // Ensure animation runs only once

  // Initialize counters state based on metricsData length, all start at 0
  const [counters, setCounters] = useState(() => metricsData.map(() => 0));

  // --- Intersection Observer Setup ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Check if intersecting and animation hasn't run yet
        if (entry.isIntersecting && !hasAnimated) {
          // console.log"Section intersecting, triggering animation.");
          setIsVisible(true);
          setHasAnimated(true); // Mark as animated
          // Optional: Unobserve after triggering to save resources
          // if (sectionRef.current) {
          //   observer.unobserve(sectionRef.current);
          // }
        }
      },
      {
        threshold: 0.2, // Trigger when 20% is visible
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    // Cleanup: Unobserve when component unmounts or hasAnimated changes
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]); // Re-run observer setup only if hasAnimated changes (e.g., for reset logic if needed)

  // --- Counter Animation Effect ---
  useEffect(() => {
    // Don't run if the section isn't visible yet
    if (!isVisible) return;

    // console.log"Starting counter animations...");
    const activeTimers = []; // To store interval IDs for cleanup

    metricsData.forEach((metric, index) => {
      const { target, duration } = metric;

      // Skip animation for target 0
      if (target === 0) {
        return;
      }

      let startTime = null;
      let timerId = null;
      const animationFrameRate = 1000 / 60; // Target ~60fps

      const step = (timestamp) => {
        if (!startTime) {
          startTime = timestamp;
        }

        const elapsedTime = timestamp - startTime;
        const progress = Math.min(elapsedTime / duration, 1); // Ensure progress doesn't exceed 1
        const easedProgress = easeOutCubic(progress); // Apply easing

        const currentValue = Math.round(target * easedProgress);

        setCounters(prevCounters => {
          // Create a new array only if the value changes
          if (prevCounters[index] !== currentValue) {
            const newCounters = [...prevCounters];
            newCounters[index] = currentValue;
            return newCounters;
          }
          return prevCounters; // No change, return previous state
        });

        if (progress < 1) {
          // Continue animation
          timerId = setTimeout(() => requestAnimationFrame(step), animationFrameRate);
          // Store timerId for potential cleanup
          // Note: Storing requestAnimationFrame IDs isn't standard, cleanup focuses on setTimeout
          // activeTimers[index] = timerId; // We need a way to manage this if mixing setTimeout & rAF
        } else {
          // Ensure final value is exactly the target
          setCounters(prevCounters => {
            const newCounters = [...prevCounters];
            if (newCounters[index] !== target) {
                newCounters[index] = target;
                return newCounters;
            }
            return prevCounters;
          });
           // console.log`Metric ${index} (${metric.id}) finished.`);
           // No need to clear `requestAnimationFrame` explicitly in this pattern
           // but clear the `setTimeout` if it exists
           clearTimeout(timerId);
        }
      };

      // Start the animation using requestAnimationFrame for smoothness
       // Use setTimeout to initiate the rAF loop to allow cleanup via clearTimeout
      timerId = setTimeout(() => requestAnimationFrame(step), 0);
      activeTimers[index] = timerId; // Store the setTimeout ID
    });

    // Cleanup function: Clear all active timers if the component unmounts
    // or if `isVisible` changes *before* animations complete.
    return () => {
      // console.log"Cleaning up animation timers...");
      activeTimers.forEach(timerId => {
        if(timerId) clearTimeout(timerId); // Use clearTimeout
      });
    };
    // Depend on isVisible and the data itself (if it could change)
  }, [isVisible, metricsData]); // metricsData included per hook rules

  // --- Formatting Function ---
  const formatValue = useCallback((index) => {
      const metric = metricsData[index];
      const currentValue = counters[index];

      // Use special display format only when animation completes AND value matches target
      if (metric.display && currentValue === metric.target) {
          return metric.display;
      }
      // Format with comma for thousands
      const formattedNumber = currentValue.toLocaleString();

      return formattedNumber + metric.suffix;
  }, [counters, metricsData]); // Depend on counters and data


  // --- Styles (using inline for simplicity as per original) ---
  const styles = {
    container: {
      backgroundColor: '#111d4a',
      color: 'white',
      padding: '60px 0',
      marginBottom: '60px',
      overflow: 'hidden',
    },
    innerContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
      marginBottom: '40px',
      textAlign: 'center',
    },
    // Base style for metric items (hidden initially)
    metricItemBase: {
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
    },
    // Style applied when visible (triggers transition)
    metricItemVisible: (index) => ({
      opacity: 1,
      transform: 'translateY(0)',
      transitionDelay: `${0.1 + index * 0.1}s`, // Stagger entrance
    }),
    valueText: {
      fontSize: 'clamp(2.5rem, 5vw, 3rem)', // Responsive font size
      fontWeight: 'normal', // Changed from 600 to normal
      margin: '0 0 10px 0',
      color: '#fff', // Ensure contrast
      position: 'relative',
      minHeight: '3.5rem', // Prevent layout shift during animation
    },
    descriptionText: {
      fontSize: '1rem',
      opacity: '0.8',
      lineHeight: '1.5',
      marginBottom: '0',
      minHeight: '3em', // Prevent layout shift
    },
    dividerBase: {
      border: '0',
      height: '1px',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      margin: '40px auto', // Center if needed
      width: '100%',
      transform: 'scaleX(0)',
      transformOrigin: 'center',
      transition: 'transform 1s ease-out 0.5s', // Delay divider animation
    },
    dividerVisible: {
      transform: 'scaleX(1)',
    },
    highlight: {
      display: 'inline-block',
      position: 'relative',
      zIndex: 1,
    },
    // Use pseudo-element for highlight if possible, but inline span is ok
    highlightBgBase: {
      position: 'absolute',
      bottom: '10%', // Adjusted position
      left: '-5%',
      width: '0%', // Start at 0 width
      height: '10%',
      backgroundColor: 'rgba(255, 255, 255, 0.15)', // Slightly subtler
      zIndex: -1,
      transition: 'width 0.8s ease-out',
      transitionDelay: '0.7s', // Delay highlight after number appears
    },
    highlightBgVisible: {
      width: '110%',
    },
  };

  // --- Render ---
  return (
    <div ref={sectionRef} style={styles.container}>
      <div style={styles.innerContainer}>
        {/* First row */}
        <div style={styles.gridContainer}>
          {metricsData.slice(0, 3).map((metric, index) => (
            <div
              key={metric.id}
              style={{
                ...styles.metricItemBase,
                ...(isVisible ? styles.metricItemVisible(index) : {}),
              }}
            >
              <h2 style={styles.valueText}>
                <span style={styles.highlight}>
                  {/* Use formatValue correctly */}
                  {formatValue(index)}
                  <span
                    style={{
                      ...styles.highlightBgBase,
                      ...(isVisible ? styles.highlightBgVisible : {}),
                    }}
                  ></span>
                </span>
              </h2>
              <p style={styles.descriptionText}>{metric.text}</p>
            </div>
          ))}
        </div>

        {/* Divider */}
        <hr
          style={{
            ...styles.dividerBase,
            ...(isVisible ? styles.dividerVisible : {}),
          }}
        />

        {/* Second row */}
        <div style={styles.gridContainer}>
          {metricsData.slice(3).map((metric, index) => {
            // Calculate the correct overall index for styling delays and formatting
            const overallIndex = index + 3;
            return (
              <div
                key={metric.id}
                style={{
                  ...styles.metricItemBase,
                  ...(isVisible ? styles.metricItemVisible(overallIndex) : {}),
                }}
              >
                <h2 style={styles.valueText}>
                  <span style={styles.highlight}>
                    {/* Use formatValue with the overall index */}
                    {formatValue(overallIndex)}
                    <span
                      style={{
                        ...styles.highlightBgBase,
                        ...(isVisible ? styles.highlightBgVisible : {}),
                      }}
                    ></span>
                  </span>
                </h2>
                <p style={styles.descriptionText}>{metric.text}</p>
              </div>
            );
          })}
        </div>

         {/* Debug Info (Optional) */}
         {/* <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px', marginTop: '20px' }}>
          Visible: {isVisible.toString()} | Animated: {hasAnimated.toString()} | Counters: {counters.join(', ')}
         </div> */}
      </div>
    </div>
  );
};

export default AnimatedMetricsSection;